// src/pages/Ordini.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Store, ShoppingBag, ChevronRight } from "lucide-react";
import OrderPeriodPicker from "../../components/OrderPeriodPicker";
import { useCustomerPresentation } from "../../utils/appMode";
import toast from "react-hot-toast";
import { listUserOrders } from "../../api/ordersApi";
import "../css/Ordini.css";

/* ---------- utils ---------- */
function formatCurrency(v) {
  const n = Number(v);
  const isCents = Number.isInteger(n) && n >= 50;
  const eur = isCents ? n / 100 : n;
  return eur.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}


function statusClass(s) {
  if (!s) return "status-badge";
  switch (String(s).toLowerCase()) {
    case "consegnata":      return "status-badge status-badge--consegnata";
    case "in_consegna":     return "status-badge status-badge--in-consegna";
    case "completata":      return "status-badge status-badge--completata";
    case "ritirata":        return "status-badge status-badge--ritirata";
    case "pagata":          return "status-badge status-badge--pagata";
    case "in_lavorazione":  return "status-badge status-badge--in-lavorazione";
    case "annullata":
    case "rifiutata":       return "status-badge status-badge--annullata";
    default:                return "status-badge";
  }
}

function statusLabel(s) {
  if (!s) return "—";
  switch (String(s).toLowerCase()) {
    case "consegnata":      return "Consegnato";
    case "in_consegna":     return "In consegna";
    case "completata":      return "Completato";
    case "ritirata":        return "Ritirato";
    case "pagata":          return "Pagato";
    case "in_lavorazione":  return "In lavorazione";
    case "annullata":       return "Annullato";
    case "rifiutata":       return "Rifiutato";
    default:                return String(s);
  }
}

function viewerRole() {
  try {
    const payload = localStorage.getItem("token")?.split(".")[1];
    const user = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return String(user.role || user.user?.role || "").toLowerCase();
  } catch { return false; }
}
function supplierImage(order) {
  const url = order?.fornitore_immagine;
  return typeof url === "string" && /^https?:\/\//i.test(url) ? url : null;
}
function OrderThumbnail({ url }) {
  return <span className="customer-order-thumb" aria-hidden="true">
    <Store size={26} />
    {url && <img key={url} src={url} alt="" loading="lazy" onError={event => { event.currentTarget.style.visibility = "hidden"; }} />}
  </span>;
}

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: "all", label: "Tutti" },
  { value: "ritir", label: "Ritirato" },
  { value: "lavor", label: "In lavorazione" },
  { value: "consegn", label: "Consegnato" },
];

export default function ElencoOrdini() {
  const token = localStorage.getItem("token");
  const role = viewerRole();
  const isCustomer = useCustomerPresentation(role);
  const [searchParams, setSearchParams] = useSearchParams();

  // UI state (form filtri)
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [from, setFrom] = useState(searchParams.get("from") || "");
  const [to, setTo] = useState(searchParams.get("to") || "");

  // Filtri applicati (quelli che usiamo nelle chiamate API)
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "all",
    from: searchParams.get("from") || "",
    to: searchParams.get("to") || "",
  });

  const [page, setPage] = useState(Number(searchParams.get("page") || 1));
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Aggiorna querystring per deep-linking
  useEffect(() => {
    const qp = new URLSearchParams();
    qp.set("page", String(page));
    if (filters.status && filters.status !== "all") {
      qp.set("status", filters.status);
    }
    if (filters.from) qp.set("from", filters.from);
    if (filters.to) qp.set("to", filters.to);
    setSearchParams(qp, { replace: true });
  }, [page, filters, setSearchParams]);

  // Fetch ordini
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");

    listUserOrders({ page, pageSize: PAGE_SIZE, filters, token })
      .then((res) => {
        if (!alive) return;
        setOrders(Array.isArray(res.data) ? res.data : []);
        setMeta({
          page: Number(res.page || page),
          totalPages: Number(res.totalPages || 1),
        });
      })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.message || "Errore nel caricamento ordini";
        setError(msg);
        toast.error("Impossibile caricare gli ordini");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [page, filters, token]);

  const hasRows = orders && orders.length > 0;
  const rows = useMemo(() => orders || [], [orders]);

  const automaticFilters = isCustomer || role === "admin";
  const invalidRange = !!(from && to && from > to);

  function commitFilters(next) {
    if (next.from && next.to && next.from > next.to) return;
    if (next.status === filters.status && next.from === filters.from && next.to === filters.to) return;
    setPage(1);
    setFilters(next);
  }

  function applyPeriod(range) {
    setFrom(range.from);
    setTo(range.to);
    commitFilters({ status, ...range });
  }

  function applyFilters() {
    setPage(1);
    setFilters({ status, from, to });
  }

  function resetFilters() {
    setStatus("all");
    setFrom("");
    setTo("");
    setPage(1);
    setFilters(prev => prev.status === "all" && !prev.from && !prev.to ? prev : { status: "all", from: "", to: "" });
  }

  return (
    <div className={`list-page list-page--orders ${isCustomer ? "customer-orders" : ""}`}>
      <header className="list-header">
        {isCustomer ? <><span className="customer-orders-title-icon"><ShoppingBag size={26} aria-hidden="true" /></span>
          <div><h1>I miei ordini</h1><p className="customer-orders-description">Consulta i tuoi ordini e verifica il loro stato.</p></div></> : <h1>I miei ordini</h1>}
      </header>

      {/* Filtri */}
      <section className="list-filters">
        <div className={`list-filterbar ${automaticFilters ? "list-filterbar--range" : ""}`}>
          <div className="list-filter-field">
            <label className="list-filter-label" htmlFor="customer-status">Stato ordine</label>
            <select
              id="customer-status"
              value={status}
              onChange={(e) => {
                const v = e.target.value;
                setStatus(v);
                if (automaticFilters) commitFilters({ status: v, ...(invalidRange ? { from: filters.from, to: filters.to } : { from, to }) });
              }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {automaticFilters ? <div className="list-filter-field">
            <span className="list-filter-label">Periodo ordini</span>
            <OrderPeriodPicker from={filters.from} to={filters.to} onApply={applyPeriod} />
          </div> : <>
          <div className="list-filter-field">
            <label className="list-filter-label" htmlFor="customer-from">Dal</label>
            <input
              type="date"
              id="customer-from"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>

          <div className="list-filter-field">
            <label className="list-filter-label" htmlFor="customer-to">Al</label>
            <input
              type="date"
              id="customer-to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>

          </>}

          <div className="list-filter-actions">
            <button className="list-filter-btn" data-filter-reset onClick={resetFilters}>
              Pulisci
            </button>
            {!automaticFilters && <button
              className="list-filter-btn list-filter-btn--cta"
              onClick={applyFilters}
            >
              Filtra
            </button>}
            {automaticFilters && invalidRange && <small role="alert">La data Dal deve precedere o coincidere con la data Al.</small>}
          </div>
        </div>
      </section>

      <main className="list-main">
        {error && !loading && (
          <p className="list-empty" style={{ marginBottom: 10 }}>
            {error}
          </p>
        )}

        {/* Tabella ordini */}
        {isCustomer ? <div className="customer-order-list" aria-label="Elenco ordini" aria-busy={loading}>
          {loading && Array.from({ length: 6 }, (_, i) => <div className="customer-order-skeleton" key={i}>Caricamento ordini…</div>)}
          {!loading && !hasRows && <div className="list-empty">Nessun ordine trovato.</div>}
          {!loading && rows.map(o => {
            const image = supplierImage(o);
            return <Link className="customer-order-card" key={o.id} to={`/ordini/${o.id}`} aria-label={`Vedi ordine ${o.id}`} data-status={o.raw_status}>
              <div className="customer-order-number"><strong>#{o.id}</strong><time>{formatDate(o.created_at || o.data || o.createdAt)}</time></div>
              <OrderThumbnail url={image} />
              <div className="customer-order-supplier"><strong>{o.fornitore || "-"}</strong><span>Fornitore</span></div>
              <span className={statusClass(o.raw_status)}>{statusLabel(o.raw_status)}</span>
              <strong className="customer-order-amount">{formatCurrency(o.total_cents ?? o.totale_cents ?? o.total_eur ?? o.total)}</strong>
              <span className="customer-order-chevron"><ChevronRight size={20} aria-hidden="true" /></span>
            </Link>;
          })}
        </div> : (
        <div className="list-table" role="table" aria-label="Elenco ordini">
          <div className="list-row list-row--head" role="row">
            <div className="list-cell list-col-orderid list-col--nr" role="columnheader">
              Nr
            </div>
            <div className="list-cell list-col--date" role="columnheader">
              Data
            </div>
            <div className="list-cell list-ta-right list-col--amount" role="columnheader">
              Importo
            </div>
            <div className="list-cell list-col--status" role="columnheader">
              Stato
            </div>
            <div className="list-cell list-col--supplier" role="columnheader">
              Fornitore
            </div>
            <div className="list-cell list-ta-center list-col--detail" role="columnheader">
              Dettaglio
            </div>
          </div>

          {loading &&
            Array.from({ length: 12 }).map((_, i) => (
              <div
                className="list-row list-skel"
                key={`sk-${i}`}
                role="row"
                aria-busy="true"
              >
                <div className="list-cell"><span className="list-skel-bar" style={{ width: "55%" }} /></div>
                <div className="list-cell"><span className="list-skel-bar" style={{ width: "80%" }} /></div>
                <div className="list-cell list-ta-right"><span className="list-skel-bar" style={{ width: "90%", marginLeft: "auto" }} /></div>
                <div className="list-cell"><span className="list-skel-bar" style={{ width: "75%" }} /></div>
                <div className="list-cell"><span className="list-skel-bar" style={{ width: `${50 + (i % 4) * 12}%` }} /></div>
                <div className="list-cell list-ta-center"><span className="list-skel-bar" style={{ width: "60%", margin: "0 auto" }} /></div>
              </div>
            ))}

          {!loading && !hasRows && (
            <div className="list-empty">Nessun ordine trovato.</div>
          )}

          {!loading &&
            hasRows &&
            rows.map((o) => (
              <div className="list-row" role="row" key={o.id}>
                <div
                  className="list-cell list-col-orderid list-col--nr"
                  role="cell"
                  data-label="Nr"
                >
                  <span className="list-orderid">{o.id}</span>
                </div>

                <div className="list-cell list-col--date" role="cell" data-label="Data">
                  {formatDate(o.created_at || o.data || o.createdAt)}
                </div>

                <div
                  className="list-cell list-ta-right list-col--amount"
                  role="cell"
                  data-label="Importo"
                >
                  {formatCurrency(
                    o.total_cents ?? o.totale_cents ?? o.total_eur ?? o.total
                  )}
                </div>

                <div className="list-cell list-col--status" role="cell" data-label="Stato">
                  <span className={statusClass(o.raw_status)}>
                    {statusLabel(o.raw_status)}
                  </span>
                </div>

                <div
                  className="list-cell list-col--supplier"
                  role="cell"
                  data-label="Fornitore"
                >
                  {o.fornitore || "—"}
                </div>

                <div
                  className="list-cell list-ta-center list-col--detail"
                  role="cell"
                  data-label="Dettaglio"
                >
                  <Link className="list-link list-link--icon" to={`/ordini/${o.id}`} aria-label={`Vedi ordine ${o.id}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  </Link>
                </div>
              </div>
            ))}
        </div>
        )}

        {/* Pager */}
        {meta.totalPages > 1 && (
          <nav className="list-pager" aria-label="Pagine ordini">
            <button
              className="list-pagerbtn"
              onClick={() =>
                setPage((prev) => Math.max(1, (meta.page || prev || 1) - 1))
              }
              aria-label="Pagina precedente"
            >
              ←
            </button>

            <div className="list-pages">
              {Array.from({ length: meta.totalPages }, (_, i) => i + 1).map(
                (p) => (
                  <button
                    key={p}
                    className={`list-pagebtn ${p === meta.page ? "is-active" : ""
                      }`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
            </div>

            <button
              className="list-pagerbtn"
              onClick={() =>
                setPage((prev) =>
                  Math.min(meta.totalPages, (meta.page || prev || 1) + 1)
                )
              }
              aria-label="Pagina successiva"
            >
              →
            </button>
          </nav>
        )}
      </main>
    </div>
  );
}
